import { useState, useEffect } from 'react';
import styled from 'styled-components';
import api from '../../../../utils/api'
import { toast } from 'react-toastify';
import { useAccountContext } from '../AdminAccountPage';
import { SamlSettings, SsoUser } from '../types';

// ========================================
// Styled Components - Matching Semantic UI
// ========================================

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 0 0 24px 0;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #85b7d9;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  font-size: 14px;
  font-family: monospace;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #85b7d9;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  
  input {
    margin-right: 8px;
    width: 17px;
    height: 17px;
  }
`;

const SubSectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  margin: 24px 0 12px 0;
`;

const InfoBox = styled.div`
  background: #f8ffff;
  border: 1px solid #a9d5de;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  
  p {
    margin: 8px 0 0 0;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.87);
  }
`;

const InfoText = styled.p`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 16px;
`;

const SelectLinks = styled.div`
  margin-bottom: 12px;
  font-size: 14px;
  
  span {
    color: rgba(0, 0, 0, 0.6);
  }
`;

const SelectLink = styled.button`
  background: none;
  border: none;
  color: #4183c4;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid rgba(34, 36, 38, 0.15);
  margin-top: 8px;
  
  th, td {
    text-align: left;
    padding: 11px 12px;
    border-top: 1px solid rgba(34, 36, 38, 0.1);
  }
  
  th {
    background: #f9fafb;
    font-size: 12px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.87);
    border-top: none;
  }
  
  tr:hover td {
    background: #f9fafb;
  }
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 16px;
  
  ${({ $primary }) => $primary ? `
    background: #2185d0;
    color: white;
    &:hover { background: #1678c2; }
  ` : `
    background: #e0e1e2;
    color: rgba(0, 0, 0, 0.6);
    &:hover { background: #cacbcd; }
  `}
  
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const UserLink = styled.a`
  color: #4183c4;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LoadingMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #666;
`;

// ========================================
// Component
// ========================================

function SsoSamlSettingsTab() {
  const { account } = useAccountContext();
  
  const [samlSettings, setSamlSettings] = useState<SamlSettings>({
    enabled: false,
    signRequests: false,
    legacySha1: false,
    metadataUrl: '',
    ssoTargetUrl: '',
    publicCert: '',
    autoEnableUsers: false,
  });
  const [eligibleUsers, setEligibleUsers] = useState<SsoUser[]>([]);
  const [enabledUserIds, setEnabledUserIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (account) {
      fetchSamlSettings();
    }
  }, [account]);

  const fetchSamlSettings = async () => {
    if (!account) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/admin2/accounts/${account.id}/saml/edit.json`);
      const data = response.data;
      
      setSamlSettings({
        enabled: data.enabled || false,
        signRequests: data.sign_requests || data.signRequests || false,
        legacySha1: data.legacy_sha1 || data.legacySha1 || false,
        metadataUrl: data.metadata_url || data.metadataUrl || '',
        ssoTargetUrl: data.sso_target_url || data.ssoTargetUrl || '',
        publicCert: data.public_cert || data.publicCert || '',
        autoEnableUsers: data.auto_enable_users || data.autoEnableUsers || false,
      });
      
      // Load eligible users
      const users = data.eligible_users || data.eligibleUsers || [];
      setEligibleUsers(users.map((u: any) => ({
        id: String(u.id),
        name: u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim(),
        email: u.email,
        ssoEnabled: u.sso_enabled || u.ssoEnabled || false,
      })));
      
      // Initialize enabled user IDs
      const enabledIds = new Set<string>();
      users.forEach((u: any) => {
        if (u.sso_enabled || u.ssoEnabled) {
          enabledIds.add(String(u.id));
        }
      });
      setEnabledUserIds(enabledIds);
    } catch (err) {
      console.error('Failed to fetch SAML settings:', err);
      toast.error('Failed to load SSO/SAML settings');
    }
    setLoading(false);
  };

  const handleChange = (field: keyof SamlSettings, value: string | boolean) => {
    setSamlSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleUserToggle = (userId: string) => {
    setEnabledUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const allIds = new Set(eligibleUsers.map(u => u.id));
    setEnabledUserIds(allIds);
  };

  const handleSelectNone = () => {
    setEnabledUserIds(new Set());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account) return;
    
    setSaving(true);
    try {
      const payload = {
        saml_settings: {
          enabled: samlSettings.enabled,
          sign_requests: samlSettings.signRequests,
          legacy_sha1: samlSettings.legacySha1,
          metadata_url: samlSettings.metadataUrl,
          sso_target_url: samlSettings.ssoTargetUrl,
          public_cert: samlSettings.publicCert,
          auto_enable_users: samlSettings.autoEnableUsers,
        },
        enabled_users: Array.from(enabledUserIds),
      };

      await api.put(`/admin2/accounts/${account.id}/saml.json`, payload);
      toast.success('SSO/SAML settings updated successfully');
      await fetchSamlSettings();
    } catch (error: any) {
      toast.error(error.response?.data?.errors?.join(', ') || 'Failed to save SSO/SAML settings');
    }
    setSaving(false);
  };

  if (loading) {
    return <LoadingMessage>Loading SSO/SAML settings...</LoadingMessage>;
  }

  return (
    <form onSubmit={handleSubmit}>
      <SectionTitle>SSO / SAML Settings</SectionTitle>
      
      <FormGroup>
        <CheckboxLabel>
          <input
            type="checkbox"
            checked={samlSettings.enabled}
            onChange={e => handleChange('enabled', e.target.checked)}
          />
          Enabled
        </CheckboxLabel>
      </FormGroup>
      
      <FormGroup>
        <CheckboxLabel>
          <input
            type="checkbox"
            checked={samlSettings.signRequests}
            onChange={e => handleChange('signRequests', e.target.checked)}
          />
          Sign requests
        </CheckboxLabel>
      </FormGroup>

      <FormGroup>
        <CheckboxLabel>
          <input
            type="checkbox"
            checked={samlSettings.legacySha1}
            onChange={e => handleChange('legacySha1', e.target.checked)}
          />
          Legacy SHA-1 signing (deprecated)
        </CheckboxLabel>
      </FormGroup>

      <FormGroup>
        <Label>Metadata url</Label>
        <Input
          type="text"
          value={samlSettings.metadataUrl}
          onChange={e => handleChange('metadataUrl', e.target.value)}
        />
      </FormGroup>
      
      <FormGroup>
        <Label>Sso target url</Label>
        <Input
          type="url"
          value={samlSettings.ssoTargetUrl}
          onChange={e => handleChange('ssoTargetUrl', e.target.value)}
        />
      </FormGroup>
      
      <FormGroup>
        <Label>Public cert</Label>
        <TextArea
          rows={18}
          value={samlSettings.publicCert}
          onChange={e => handleChange('publicCert', e.target.value)}
          placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
        />
      </FormGroup>
      
      <SubSectionTitle>SSO eligible users ({eligibleUsers.length})</SubSectionTitle>
      
      <InfoText>
        This is the list of users who are eligible for SSO based on their email address. You can use
        this list to turn SSO on and off for specific users if necessary. Note that SSO must first
        be enabled for this account at the top of the page.
      </InfoText>
      
      <InfoBox>
        <CheckboxLabel>
          <input
            type="checkbox"
            checked={samlSettings.autoEnableUsers}
            onChange={e => handleChange('autoEnableUsers', e.target.checked)}
          />
          Automatically enable new users
        </CheckboxLabel>
        <p>
          When a new user is created, SSO will automatically be enabled for that user if their
          email address matches the email domains assigned to this account. If you turn this
          option off, you will need to use this list to manually enable SSO each time a new user is
          created. Note that SSO must be enabled for this account at the top of the page.
        </p>
      </InfoBox>
      
      <SelectLinks>
        <span>Select: </span>
        <SelectLink type="button" onClick={handleSelectAll}>all</SelectLink>
        <span> | </span>
        <SelectLink type="button" onClick={handleSelectNone}>none</SelectLink>
      </SelectLinks>
      
      {eligibleUsers.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>Id</th>
              <th>Name</th>
              <th>Email</th>
              <th>SSO Enabled?</th>
            </tr>
          </thead>
          <tbody>
            {eligibleUsers.map(user => (
              <tr key={user.id}>
                <td>{user.id}</td>
                <td>
                  <UserLink href={`/admin2/users/${user.id}`} target="_blank">
                    {user.name} (sso)
                  </UserLink>
                </td>
                <td>{user.email}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={enabledUserIds.has(user.id)}
                    onChange={() => handleUserToggle(user.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      
      {eligibleUsers.length === 0 && (
        <InfoText>No eligible users found for SSO.</InfoText>
      )}
      
      <Button type="submit" $primary disabled={saving}>
        {saving ? 'Saving...' : 'Save'}
      </Button>
    </form>
  );
}

export default SsoSamlSettingsTab;
